import React from "react";
import { Dialog, Typography, Button, Box } from "@mui/material";

const TestSuccessAlertPage = ({ visible, title, message, onClose }) => {
  if (!visible) return null;

  return (
    <Dialog
      open={visible}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow:
            "0 32px 80px rgba(10,40,26,0.16), 0 2px 8px rgba(0,0,0,0.06)",
          animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
          "@keyframes popIn": {
            from: { opacity: 0, transform: "scale(0.85) translateY(20px)" },
            to: { opacity: 1, transform: "scale(1) translateY(0)" },
          },
        },
      }}
    >
      {/* Animated top stripe */}
      <Box
        sx={{
          height: 4,
          background:
            "linear-gradient(90deg,#22c55e,#4ade80,#86efac,#22d3ee,#22c55e)",
          backgroundSize: "300% 100%",
          animation: "stripeSlide 3s linear infinite",
          "@keyframes stripeSlide": { to: { backgroundPosition: "-300% 0" } },
        }}
      />

      {/* HEADER */}
      <Box
        sx={{
          px: 4,
          pt: 4.5,
          pb: 0,
          textAlign: "center",
          background: "linear-gradient(180deg,#f0fdf4 0%,#fff 100%)",
          borderBottom: "1px solid rgba(34,197,94,0.1)",
        }}
      >
        {/* Spinning trophy ring */}
        <Box
          sx={{
            position: "relative",
            width: 90,
            height: 90,
            mx: "auto",
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "2px solid transparent",
              background:
                "linear-gradient(#f0fdf4,#f0fdf4) padding-box, conic-gradient(from 0deg,#22c55e,#4ade80,#22d3ee,#22c55e) border-box",
              animation: "spinRing 5s linear infinite",
              "@keyframes spinRing": { to: { transform: "rotate(360deg)" } },
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 10,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#dcfce7,#bbf7d0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "2.2rem",
              boxShadow: "0 6px 20px rgba(34,197,94,0.25)",
              animation: "trophyBounce 2s ease-in-out infinite",
              "@keyframes trophyBounce": {
                "0%,100%": { transform: "scale(1) translateY(0)" },
                "50%": { transform: "scale(1.06) translateY(-3px)" },
              },
            }}
          >
            🏆
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            fontSize: "1.75rem",
            fontWeight: 700,
            color: "#052e16",
            letterSpacing: "-0.025em",
            mb: 1,
          }}
        >
          {title || "You Passed!"}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.88rem",
            color: "rgba(5,46,22,0.5)",
            lineHeight: 1.65,
            maxWidth: 320,
            mx: "auto",
          }}
        >
          {message ||
            "Outstanding work. You've demonstrated strong command of the subject matter."}
        </Typography>
      </Box>

      {/* ACHIEVEMENTS BODY */}
      <Box sx={{ px: 3.5, py: 3 }}>
        {/* Section label */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "#22c55e",
            }}
          />
          <Typography
            sx={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.66rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#16a34a",
              fontWeight: 500,
            }}
          >
            Achievements Unlocked
          </Typography>
        </Box>

        {/* Badge grid */}
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          {[
            {
              icon: "✅",
              val: "Test Passed",
              desc: "First attempt",
              bg: "rgba(34,197,94,0.06)",
              border: "rgba(34,197,94,0.2)",
              color: "#15803d",
            },
            {
              icon: "⚡",
              val: "High Score",
              desc: "Above 80%",
              bg: "rgba(34,211,238,0.06)",
              border: "rgba(34,211,238,0.2)",
              color: "#0e7490",
            },
            {
              icon: "🎯",
              val: "Focused",
              desc: "No skipped qs",
              bg: "rgba(99,60,180,0.05)",
              border: "rgba(99,60,180,0.15)",
              color: "#633cb4",
            },
          ].map(({ icon, val, desc, bg, border, color }) => (
            <Box
              key={val}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 120,
                p: "9px 14px",
                borderRadius: "10px",
                bgcolor: bg,
                border: `1px solid ${border}`,
              }}
            >
              <Typography sx={{ fontSize: "1rem" }}>{icon}</Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color,
                    lineHeight: 1.3,
                  }}
                >
                  {val}
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", color, opacity: 0.65 }}>
                  {desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* FOOTER */}
      <Box sx={{ px: 3.5, pb: 3.5, display: "flex", gap: 1.25 }}>
        <Button
          onClick={onClose}
          sx={{
            flex: 2,
            py: 1.5,
            borderRadius: "11px",
            border: "none",
            background: "linear-gradient(135deg,#16a34a,#22c55e)",
            color: "#fff",
            fontFamily: "'Sora',sans-serif",
            fontSize: "0.9rem",
            fontWeight: 700,
            boxShadow: "0 4px 18px rgba(34,197,94,0.35)",
            "&:hover": {
              boxShadow: "0 6px 28px rgba(34,197,94,0.5)",
              transform: "translateY(-2px)",
              background: "linear-gradient(135deg,#16a34a,#22c55e)",
            },
            transition: "all 0.2s",
          }}
        >
          Continue →
        </Button>
      </Box>
    </Dialog>
  );
};

export default TestSuccessAlertPage;
